<template>
  <div class="module-container">
    <h3>博客管理</h3>

    <!-- 消息提示 -->
    <div v-if="message" class="success-message">{{ message }}</div>
    <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

    <!-- 博客列表视图 -->
    <div v-if="!showBlogForm" class="blog-manager">
      <div class="action-bar">
        <button class="action-btn" @click="createNewBlog">新增博客</button>
        <div class="search-box">
          <input type="text" v-model="blogSearchQuery" placeholder="搜索博客..." class="search-input" @input="searchBlogs">
          <span class="search-icon">🔍</span>
        </div>
      </div>

      <!-- 加载指示器 -->
      <div v-if="isLoading" class="loading-indicator">
        <div class="spinner"></div>
        <p>加载中...</p>
      </div>

      <!-- 博客列表 -->
      <div v-else-if="filteredBlogs.length > 0" class="blog-list">
        <table class="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>名称</th>
              <th>标题</th>
              <th>状态</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="blog in filteredBlogs" :key="blog.id">
              <td>{{ blog.id }}</td>
              <td class="blog-title">{{ blog.name }}</td>
              <td class="blog-title">{{ blog.title || '-' }}</td>
              <td>
                <span class="status-badge" :class="blog.status">
                  {{ blog.status ? statusLabels[blog.status] : '已发布' }}
                </span>
              </td>
              <td>{{ formatDate(blog.createdAt) }}</td>
              <td class="action-cell">
                <button class="table-btn view-btn" @click="viewBlog(blog)" title="在新窗口中查看博客">浏览</button>
                <button class="table-btn edit-btn" @click="editBlog(blog)">编辑</button>
                <button class="table-btn delete-btn" @click="confirmDeleteBlog(blog)">删除</button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- 分页控件 -->
        <div class="pagination">
          <button class="page-btn" :disabled="currentPage === 1" @click="currentPage--">上一页</button>
          <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="currentPage++">下一页</button>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else class="empty-state">
        <div class="empty-icon">📝</div>
        <p>暂无博客数据，请添加新博客</p>
      </div>
    </div>

    <!-- 博客表单 -->
    <div v-else class="blog-form">
      <h4>{{ editingBlog ? '编辑博客' : '新增博客' }}</h4>

      <div class="form-group">
        <label for="blogName">博客名称 (URL 路径)</label>
        <input type="text" id="blogName" v-model="blogForm.name" placeholder="请输入博客名称" class="form-input">
        <small class="form-note">这将用作 URL 路径，例如: /blog/{{ blogForm.name }}</small>
      </div>

      <div class="form-group">
        <label for="blogTitle">显示标题</label>
        <input type="text" id="blogTitle" v-model="blogForm.title" placeholder="请输入博客标题" class="form-input">
      </div>

      <div class="form-group">
        <label for="blogDescription">博客描述</label>
        <textarea id="blogDescription" v-model="blogForm.description" placeholder="请输入博客描述" rows="3"
          class="form-input"></textarea>
      </div>

      <div class="form-group">
        <label for="blogTags">标签</label>
        <div class="tags-input-container">
          <input type="text" id="blogTags" v-model="tagInput" @keydown.enter.prevent="addTag" placeholder="输入标签后按回车添加"
            class="form-input">
          <div class="tags-container">
            <span v-for="(tag, index) in blogForm.tags" :key="index" class="tag">
              {{ tag }}
              <button @click="removeTag(index)" class="tag-remove">&times;</button>
            </span>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label for="blogStatus">状态</label>
        <select id="blogStatus" v-model="blogForm.status" class="form-input">
          <option value="draft">草稿</option>
          <option value="published">已发布</option>
        </select>
      </div>

      <div class="form-group">
        <label for="featuredImage">特色图片</label>
        <input type="text" id="featuredImage" v-model="blogForm.featuredImage" placeholder="请输入图片URL"
          class="form-input">
        <div v-if="blogForm.featuredImage" class="image-preview">
          <img :src="blogForm.featuredImage" alt="预览图" class="preview-img">
        </div>
      </div>

      <div class="form-group">
        <div class="editor-header">
          <label for="blogHtml">HTML 内容</label>
          <div class="editor-actions">
            <button type="button" @click="toggleDarkMode" class="mode-toggle-button" :class="{ 'active': isDarkMode }">
              {{ isDarkMode ? '切换亮色模式' : '切换暗色模式' }}
            </button>
            <button type="button" @click="toggleHtmlEditor" class="toggle-html-button">
              {{ showHtmlEditor ? '查看/编辑 HTML 代码' : '使用文本编辑器' }}
            </button>
          </div>
        </div>

        <QuillEditor v-if="showHtmlEditor" v-model:content="blogForm.html" :options="editorOptions" contentType="html"
          theme="snow" class="html-editor" :class="{ 'dark-mode': isDarkMode }" />

        <textarea v-else id="blogHtml" v-model="blogForm.html" placeholder="请输入 HTML 内容" rows="15" class="code-editor"
          :class="{ 'dark-mode': isDarkMode }"></textarea>
      </div>

      <div class="form-group">
        <label for="blogCss">CSS 样式</label>
        <textarea id="blogCss" v-model="blogForm.css" placeholder="请输入 CSS 样式" rows="10" class="code-editor"></textarea>
      </div>

      <div class="form-actions">
        <button class="cancel-btn" @click="cancelBlogEdit">取消</button>
        <button class="submit-btn" @click="saveBlog" :disabled="isLoading">
          {{ isLoading ? '保存中...' : '保存' }}
        </button>
      </div>
    </div>

    <!-- 删除确认对话框 -->
    <div v-if="showDeleteConfirm" class="modal-overlay">
      <div class="modal-container">
        <h4>确认删除</h4>
        <p>确定要删除博客 "{{ blogToDelete.name }}" 吗？此操作不可撤销。</p>
        <div class="modal-actions">
          <button class="cancel-btn" @click="showDeleteConfirm = false">取消</button>
          <button class="delete-btn" @click="deleteBlog" :disabled="isLoading">
            {{ isLoading ? '删除中...' : '删除' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject } from 'vue'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'

// 获取 API 基础 URL
const apiBaseUrl = inject('apiBaseUrl')

// 状态管理
const isLoading = ref(false)
const message = ref('')
const errorMessage = ref('')

// 博客管理状态
const blogs = ref([])
const filteredBlogs = ref([])
const blogSearchQuery = ref('')
const showBlogForm = ref(false)
const editingBlog = ref(null)
const tagInput = ref('')
const blogForm = ref({
  id: '',
  name: '',
  title: '',
  description: '',
  html: '',
  css: '',
  tags: [],
  status: 'draft',
  featuredImage: '',
  createdAt: ''
})
const currentPage = ref(1)
const itemsPerPage = 10
const showDeleteConfirm = ref(false)
const blogToDelete = ref(null)
const showHtmlEditor = ref(true) // 控制是否显示富文本编辑器
const isDarkMode = ref(true) // 默认使用暗色模式

// Quill 编辑器配置
const editorOptions = computed(() => ({
  theme: 'snow',
  modules: {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'script': 'sub' }, { 'script': 'super' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link', 'image', 'video']
    ],
    clipboard: {
      matchVisual: false
    }
  },
  formats: {
    image: {
      attributes: {
        style: 'max-width: 100%; height: auto; object-fit: cover;'
      }
    }
  },
  clipboard: {
    matchVisual: false
  },
  // 添加 MutationObserver 选项，避免使用已弃用的 DOM 变异事件
  bounds: document.body,
  history: {
    userOnly: true
  }
}))

// 切换 HTML 编辑器
const toggleHtmlEditor = () => {
  showHtmlEditor.value = !showHtmlEditor.value
}

// 切换暗色模式
const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value
}

// 计算总页数
const totalPages = computed(() => {
  return Math.ceil(filteredBlogs.value.length / itemsPerPage)
})

// 加载博客列表
const loadBlogs = async () => {
  isLoading.value = true
  message.value = '加载博客列表...'
  errorMessage.value = ''

  try {
    const response = await fetch(`${apiBaseUrl}/API/readJson.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({ jsonUrl: 'blogs.json' })
    })

    const data = await response.json()
    if (!response.ok) {
      throw new Error(data?.message || `HTTP ${response.status}`)
    }

    if (data && data.blogs) {
      blogs.value = data.blogs.map(blog => ({
        ...blog,
        createdAt: blog.createdAt || new Date().toISOString()
      }))
      message.value = '博客列表加载成功'
    } else {
      blogs.value = []
      message.value = '没有找到博客数据'
    }

    // 初始化过滤后的博客列表
    searchBlogs()
  } catch (error) {
    errorMessage.value = '加载博客列表失败'
    console.error('加载错误:', error)
  } finally {
    isLoading.value = false
    setTimeout(() => {
      message.value = ''
    }, 3000)
  }
}

// 状态标签映射
const statusLabels = {
  draft: '草稿',
  published: '已发布'
}

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '-'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return '-' // 检查日期是否有效

    return new Intl.DateTimeFormat('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(date)
  } catch (error) {
    console.error('日期格式化错误:', error)
    return '-'
  }
}

// 搜索博客
const searchBlogs = () => {
  if (!blogSearchQuery.value) {
    filteredBlogs.value = [...blogs.value]
  } else {
    const query = blogSearchQuery.value.toLowerCase()
    filteredBlogs.value = blogs.value.filter(blog =>
      blog.name.toLowerCase().includes(query) ||
      (blog.title && blog.title.toLowerCase().includes(query)) ||
      (blog.description && blog.description.toLowerCase().includes(query)) ||
      (blog.html && blog.html.toLowerCase().includes(query)) ||
      (blog.css && blog.css.toLowerCase().includes(query)) ||
      (blog.tags && blog.tags.some(tag => tag.toLowerCase().includes(query)))
    )
  }
  currentPage.value = 1
}

// 添加标签
const addTag = () => {
  const tag = tagInput.value.trim()
  if (tag && !blogForm.value.tags.includes(tag)) {
    blogForm.value.tags.push(tag)
    tagInput.value = ''
  }
}

// 移除标签
const removeTag = (index) => {
  blogForm.value.tags.splice(index, 1)
}

// 创建新博客
const createNewBlog = () => {
  editingBlog.value = null
  blogForm.value = {
    id: '',
    name: '',
    title: '',
    description: '',
    html: '',
    css: '',
    tags: [],
    status: 'draft',
    featuredImage: '',
    createdAt: new Date().toISOString()
  }
  showBlogForm.value = true
}

// 编辑博客
const editBlog = (blog) => {
  editingBlog.value = blog
  blogForm.value = {
    ...blog,
    tags: blog.tags || [],
    status: blog.status || 'published',
    title: blog.title || '',
    description: blog.description || '',
    featuredImage: blog.featuredImage || '',
    createdAt: blog.createdAt || new Date().toISOString()
  }
  showBlogForm.value = true
}

// 取消编辑
const cancelBlogEdit = () => {
  showBlogForm.value = false
}

// 保存博客
const saveBlog = async () => {
  if (!blogForm.value.name) {
    alert('请输入博客名称')
    return
  }

  isLoading.value = true
  message.value = editingBlog.value ? '更新博客中...' : '创建博客中...'
  errorMessage.value = ''

  try {
    // 准备要保存的博客数据
    const blogData = {
      ...blogForm.value,
      id: editingBlog.value ? editingBlog.value.id : Date.now().toString(),
      createdAt: editingBlog.value ? editingBlog.value.createdAt : new Date().toISOString()
    }

    // 更新本地数组
    if (editingBlog.value) {
      const index = blogs.value.findIndex(b => b.id === editingBlog.value.id)
      if (index !== -1) {
        blogs.value[index] = blogData
      }
    } else {
      blogs.value.push(blogData)
    }

    // 准备要上传的数据
    const jsonData = JSON.stringify({ blogs: blogs.value })
    const blob = new Blob([jsonData], { type: 'application/json' })
    const formData = new FormData()
    formData.append('file', blob, 'blogs.json')
    formData.append('filename', 'blogs.json')

    const response = await fetch(`${apiBaseUrl}/API/uploadJson.php`, {
      method: 'POST',
      body: formData
    })

    const data = await response.json()

    if (data.success) {
      message.value = editingBlog.value ? '博客更新成功' : '博客创建成功'
      showBlogForm.value = false
      editingBlog.value = null
      searchBlogs()
    } else {
      throw new Error(data.message || '保存失败')
    }
  } catch (error) {
    errorMessage.value = `保存博客失败: ${error.message}`
    console.error('保存错误:', error)
  } finally {
    isLoading.value = false
    setTimeout(() => {
      message.value = ''
    }, 3000)
  }
}

// 确认删除博客
const confirmDeleteBlog = (blog) => {
  blogToDelete.value = blog
  showDeleteConfirm.value = true
}

// 删除博客
const deleteBlog = async () => {
  isLoading.value = true
  message.value = '删除博客中...'
  errorMessage.value = ''

  try {
    // 从本地数组中移除
    blogs.value = blogs.value.filter(b => b.id !== blogToDelete.value.id)

    // 准备要上传的数据
    const jsonData = JSON.stringify({ blogs: blogs.value })
    const blob = new Blob([jsonData], { type: 'application/json' })

    // 创建 FormData 对象
    const formData = new FormData()
    formData.append('file', blob, 'blogs.json')
    formData.append('filename', 'blogs.json')

    // 保存到服务器
    const response = await fetch(`${apiBaseUrl}/API/uploadJson.php`, {
      method: 'POST',
      body: formData
    })

    const data = await response.json()

    if (data.success) {
      message.value = '博客删除成功'
      showDeleteConfirm.value = false
      blogToDelete.value = null
      searchBlogs()
    } else {
      throw new Error(data.message || '删除失败')
    }
  } catch (error) {
    errorMessage.value = `删除博客失败: ${error.message}`
    console.error('删除错误:', error)
  } finally {
    isLoading.value = false
    setTimeout(() => {
      message.value = ''
    }, 3000)
  }
}

// 查看博客
const viewBlog = (blog) => {
  const blogUrl = `${window.location.origin}/blog/${blog.name}`
  window.open(blogUrl, '_blank')
}

// 暴露方法给父组件
defineExpose({
  loadBlogs
})
</script>

<style>
/* 全局样式，不使用 scoped */
.html-editor.dark-mode .ql-editor {
  background-color: #1e1e1e !important;
  color: #e0e0e0 !important;
}

.html-editor.dark-mode .ql-toolbar {
  background-color: #2d2d2d !important;
  border-color: #444 !important;
}

.html-editor.dark-mode .ql-stroke {
  stroke: #e0e0e0 !important;
}

.html-editor.dark-mode .ql-fill {
  fill: #e0e0e0 !important;
}

.html-editor.dark-mode .ql-picker {
  color: #e0e0e0 !important;
}

.html-editor.dark-mode .ql-picker-options {
  background-color: #2d2d2d !important;
  border-color: #444 !important;
}

.html-editor.dark-mode .ql-tooltip {
  background-color: #2d2d2d !important;
  color: #e0e0e0 !important;
  border-color: #444 !important;
}

.html-editor.dark-mode .ql-picker-item {
  color: #e0e0e0 !important;
}
</style>

<style scoped>
/* 博客管理样式 */
.module-container {
  background-color: #fff;
  border-radius: 5px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 20px;
}

.action-bar {
  display: flex;
  justify-content: space-between;
  margin: 20px 0;
}

.action-btn {
  padding: 10px 20px;
  background-color: #FFD700;
  color: #333;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s;
}

.action-btn:hover {
  background-color: #f0c800;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.search-box {
  position: relative;
}

.search-input {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  width: 250px;
}

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
}

.blog-manager {
  width: 100%;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  overflow: hidden;
  color: #333;
}

.data-table th,
.data-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.data-table th {
  background-color: #f8f8f8;
  font-weight: bold;
  color: #333;
}

.data-table tr:hover {
  background-color: #f9f9f9;
}

.blog-title {
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.blog-content {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: monospace;
  font-size: 12px;
  color: #666;
}

.action-cell {
  white-space: nowrap;
}

.table-btn {
  padding: 5px 10px;
  margin-right: 5px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.view-btn {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.view-btn:hover {
  background-color: #c8e6c9;
}

.edit-btn {
  background-color: #e3f2fd;
  color: #1976d2;
}

.edit-btn:hover {
  background-color: #bbdefb;
}

.delete-btn {
  background-color: #ffebee;
  color: #d32f2f;
}

.delete-btn:hover {
  background-color: #ffcdd2;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
}

.page-btn {
  padding: 6px 12px;
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background-color: #e0e0e0;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  margin: 0 15px;
  font-size: 14px;
  color: #666;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 0;
  color: #999;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 15px;
  opacity: 0.5;
}

/* 博客表单样式 */
.blog-form {
  background-color: white;
  border-radius: 5px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.blog-form h4 {
  margin-top: 0;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #333;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s;
  background-color: #fff;
  color: #333;
}

.form-group input:focus,
.form-group textarea:focus {
  border-color: #FFD700;
  outline: none;
  box-shadow: 0 0 5px rgba(255, 215, 0, 0.3);
}

.form-note {
  display: block;
  margin-top: 5px;
  color: #414141;
  font-size: 12px;
}

.editor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.editor-actions {
  display: flex;
  gap: 10px;
}

.toggle-html-button,
.mode-toggle-button {
  background-color: #e3f2fd;
  color: #1976d2;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.toggle-html-button:hover,
.mode-toggle-button:hover {
  background-color: #bbdefb;
}

.mode-toggle-button {
  background-color: #f0f0f0;
  color: #333;
}

.mode-toggle-button.active {
  background-color: #333;
  color: #fff;
}

.mode-toggle-button:hover {
  background-color: #e0e0e0;
}

.mode-toggle-button.active:hover {
  background-color: #444;
}

.html-editor {
  height: 400px;
  margin-bottom: 20px;
  border-radius: 4px;
  overflow: hidden;
}

.code-editor {
  font-family: monospace;
  font-size: 14px;
  line-height: 1.5;
  tab-size: 2;
  background-color: #f8f8f8;
}

.code-editor.dark-mode {
  background-color: #1e1e1e;
  color: #e0e0e0;
  border-color: #444;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.cancel-btn {
  padding: 10px 20px;
  background-color: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
}

.cancel-btn:hover {
  background-color: #e0e0e0;
}

.submit-btn {
  padding: 10px 20px;
  background-color: #FFD700;
  color: #333;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
}

.submit-btn:hover {
  background-color: #f0c800;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

/* 模态框样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-container {
  background-color: white;
  border-radius: 5px;
  padding: 20px;
  width: 400px;
  max-width: 90%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.modal-container h4 {
  margin-top: 0;
  color: #333;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

/* 加载指示器样式 */
.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 215, 0, 0.3);
  border-radius: 50%;
  border-top-color: #FFD700;
  animation: spin 1s ease-in-out infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.success-message {
  background-color: rgba(0, 128, 0, 0.1);
  color: #2e7d32;
  padding: 10px 15px;
  border-radius: 5px;
  margin-bottom: 15px;
  border-left: 4px solid #2e7d32;
}

.error-message {
  background-color: rgba(255, 0, 0, 0.1);
  color: #d32f2f;
  padding: 10px 15px;
  border-radius: 5px;
  margin-bottom: 15px;
  border-left: 4px solid #d32f2f;
}

.tags-input-container {
  margin-top: 8px;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.tag {
  background-color: rgba(255, 215, 0, 0.1);
  border: 1px solid rgba(255, 215, 0, 0.2);
  color: #333;
  padding: 4px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.tag-remove {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0 4px;
}

.tag-remove:hover {
  color: #d32f2f;
}

.image-preview {
  margin-top: 8px;
  max-width: 300px;
}

.preview-img {
  width: 100%;
  height: auto;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.form-input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: all 0.3s;
}

.form-input:focus {
  border-color: #FFD700;
  outline: none;
  box-shadow: 0 0 5px rgba(255, 215, 0, 0.3);
}

select.form-input {
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 1em;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.draft {
  background-color: #fff3e0;
  color: #e65100;
}

.status-badge.published {
  background-color: #e8f5e9;
  color: #2e7d32;
}
</style>
